/* ============================================================
   YU — THEME BEHAVIOR
   yu-translation-spec.md v4 §4.1-§4.7.

   Vanilla JS. No framework, no bundler, no dependency. Defer-loaded.
   Every behavior is progressive: with JS disabled the page still renders
   and every link still navigates.

   NOT PORTED (§0 "What is NOT ported"): the React runtime, _ds_bundle.js,
   support.js, window.YUDesignSystem_3fb89f, window.YUData, the App.jsx
   client router, data.js, index.html / mobile-preview.html, the
   #yuk-scroll preview scroll container, and any data-view mobile lock.

   CURRENCY (§5.4, operator ruling 7): there is NO money formatter in this
   file, no currency symbol, and no currency-symbol data attribute. Every
   price is rendered server-side through | money — the cart drawer and the
   search results are both re-rendered as MARKUP, never as raw numbers.

   STORAGE (§4.4, §4.9): `yu_quizbar_dismissed` is the ONLY localStorage
   key in this theme, yu-quiz.js included. Nothing else is persisted.
   ============================================================ */
(function () {
  'use strict';

  var QUIZBAR_KEY = 'yu_quizbar_dismissed';
  var SEARCH_DEBOUNCE_MS = 200;
  var FOCUS_DELAY_MS = 30;
  var SECTION_ID = 'cart-drawer';
  var MOBILE_QUERY = '(max-width: 760px)';
  /* R10 — the desktop side of the theme's 760/761 split (yu-mobile.css). */
  var DESKTOP_QUERY = '(min-width: 761px)';

  /* ---------- small helpers ---------- */
  function qs(sel, root) { return (root || document).querySelector(sel); }
  function qsa(sel, root) { return Array.prototype.slice.call((root || document).querySelectorAll(sel)); }

  function onReady(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn);
    } else {
      fn();
    }
  }

  var FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  function trapFocus(container, event) {
    var items = qsa(FOCUSABLE, container).filter(function (el) {
      return el.offsetParent !== null;
    });
    if (!items.length) return;
    var first = items[0];
    var last = items[items.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  /* ============================================================
     §4.1 CART DRAWER
     Add: POST /cart/add.js, then re-render through the Section Rendering
     API (?sections=cart-drawer) and open. Qty/remove: POST /cart/change.js
     with `line`. Close: X, backdrop, Escape. Focus moves to the close
     button on open and returns to the trigger on close, and is trapped
     while open. NEVER auto-opens on page load.
     ============================================================ */
  var cart = {
    lastTrigger: null,

    root: function () { return qs('[data-yu-cart-drawer]'); },

    isOpen: function () {
      var r = this.root();
      return !!r && r.classList.contains('is-open');
    },

    open: function (trigger) {
      var r = this.root();
      if (!r) return;
      this.lastTrigger = trigger || document.activeElement;
      r.classList.add('is-open');
      r.setAttribute('aria-hidden', 'false');
      var close = qs('[data-yu-cart-close]', r);
      if (close) close.focus();
    },

    close: function () {
      var r = this.root();
      if (!r) return;
      r.classList.remove('is-open');
      r.setAttribute('aria-hidden', 'true');
      if (this.lastTrigger && typeof this.lastTrigger.focus === 'function') {
        this.lastTrigger.focus();
      }
      this.lastTrigger = null;
    },

    /* Swap in freshly rendered drawer markup returned by BUNDLED SECTION
       RENDERING (the `sections` parameter on the Cart AJAX endpoints). The
       server response already contains every price through | money, so no
       client-side money handling exists.

       Bundled rendering is used instead of a separate
       `?sections=cart-drawer` page request because cart-drawer is rendered
       from layout/theme.liquid, and it arrives in the SAME response as the
       mutation — one round trip, and no second request that can fail
       silently after the cart has already changed. */
    applySections: function (data) {
      var html = data && data.sections && data.sections[SECTION_ID];
      if (!html) return false;
      var parsed = new DOMParser().parseFromString(html, 'text/html');
      var fresh = parsed.querySelector('[data-yu-cart-drawer]');
      var current = this.root();
      if (!fresh || !current) return false;
      var wasOpen = current.classList.contains('is-open');
      current.replaceWith(fresh);
      if (wasOpen) {
        fresh.classList.add('is-open');
        fresh.setAttribute('aria-hidden', 'false');
      }
      return true;
    },

    setCount: function (count) {
      if (typeof count !== 'number') return;
      qsa('[data-yu-cart-count]').forEach(function (el) {
        el.textContent = count;
        if (count > 0) {
          el.removeAttribute('hidden');
        } else {
          el.setAttribute('hidden', '');
        }
      });
    },

    /* Fallback only: used when a mutation response carries no item_count. */
    syncCount: function () {
      var self = this;
      fetch(window.Shopify && window.Shopify.routes ? window.Shopify.routes.root + 'cart.js' : '/cart.js', {
        headers: { 'Accept': 'application/json' }
      })
        .then(function (res) { return res.json(); })
        .then(function (data) { self.setCount(data.item_count); })
        .catch(function () {});
    },

    add: function (form, trigger) {
      var self = this;
      var body = new FormData(form);
      body.append('sections', SECTION_ID);
      return fetch('/cart/add.js', {
        method: 'POST',
        body: body,
        headers: { 'Accept': 'application/json' }
      })
        .then(function (res) {
          if (!res.ok) throw new Error('add failed');
          return res.json();
        })
        .then(function (data) {
          self.applySections(data);
          self.open(trigger);
          /* /cart/add.js returns the added LINE, not the cart, so the count
             is read back separately. */
          self.syncCount();
        })
        .catch(function () {
          /* Fall back to a real navigation so the action never silently
             fails with JS half-working. */
          form.submit();
        });
    },

    change: function (line, quantity) {
      var self = this;
      return fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({
          line: Number(line),
          quantity: Number(quantity),
          sections: SECTION_ID
        })
      })
        .then(function (res) { return res.json(); })
        .then(function (data) {
          self.applySections(data);
          /* /cart/change.js returns the whole cart. */
          if (data && typeof data.item_count === 'number') {
            self.setCount(data.item_count);
          } else {
            self.syncCount();
          }
        })
        .catch(function () {});
    }
  };

  function bindCart() {
    /* Delegated so a re-rendered drawer needs no rebinding. */
    document.addEventListener('click', function (e) {
      var openBtn = e.target.closest('[data-yu-cart-open]');
      if (openBtn) {
        e.preventDefault();
        /* The drawer is rendered server-side on every page load and is
           already current, so opening needs no request. Re-rendering here
           was what made the trigger fail silently when the fetch did. */
        cart.open(openBtn);
        return;
      }

      if (e.target.closest('[data-yu-cart-close], [data-yu-cart-close-backdrop]')) {
        e.preventDefault();
        cart.close();
        return;
      }

      var qtyBtn = e.target.closest('[data-yu-cart-qty]');
      if (qtyBtn) {
        e.preventDefault();
        cart.change(qtyBtn.getAttribute('data-yu-cart-qty'), qtyBtn.getAttribute('data-yu-cart-qty-to'));
      }
    });

    document.addEventListener('submit', function (e) {
      var form = e.target.closest('[data-yu-add-form]');
      if (!form) return;
      e.preventDefault();
      cart.add(form, form.querySelector('[type="submit"]'));
    });

    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape' || !cart.isOpen()) return;
      cart.close();
    });

    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Tab' || !cart.isOpen()) return;
      var panel = qs('.yuk-cart__panel', cart.root());
      if (panel) trapFocus(panel, e);
    });
  }

  /* ============================================================
     §4.2 INLINE HEADER SEARCH
     Shopify predictive search, 200ms debounce. Desktop swaps the nav for
     the field; mobile reveals the full-width field beneath the header row.
     Escape and backdrop close and clear. Focus moves into the field 30ms
     after reveal, as in the kit.

     The mobile field must render at FULL SIZE — the 0x0 regression is an
     explicit acceptance check. Visibility is a display toggle only; no
     height or width is ever animated, set, or measured here.

     Results are fetched as SERVER-RENDERED MARKUP (section_id), so prices
     arrive already through | money and this file contains no currency code.
     ============================================================ */
  function bindSearch() {
    var header = qs('[data-yu-header]');
    if (!header) return;

    var toggle = qs('[data-yu-search-toggle]', header);
    var mobileWrap = qs('[data-yu-mobile-search]', header);
    var results = qs('[data-yu-search-results]', header);
    var resultsInner = qs('[data-yu-search-results-inner]', header);
    var timer = null;
    var lastQuery = '';
    /* The spec's empty state is rendered in the markup (see header.liquid),
       so the copy lives in Liquid and is simply restored here — never
       rebuilt in JS, and never fetched with an empty q. */
    var emptyMarkup = resultsInner ? resultsInner.innerHTML : '';

    function isMobile() {
      return window.matchMedia(MOBILE_QUERY).matches;
    }

    function activeInput() {
      return isMobile() ? qs('#yu-search-mobile', header) : qs('#yu-search-desktop', header);
    }

    function showResults() {
      if (results) results.removeAttribute('hidden');
    }

    function hideResults() {
      if (results) results.setAttribute('hidden', '');
    }

    function openSearch() {
      header.classList.add('is-searching');
      /* Only the mobile breakpoint gets the full-width field; at desktop the
         header field is the one field. Adding is-open unconditionally is
         what rendered both at once. */
      if (mobileWrap && isMobile()) mobileWrap.classList.add('is-open');
      if (toggle) toggle.setAttribute('aria-expanded', 'true');
      showResults();
      renderEmpty();
      window.setTimeout(function () {
        var input = activeInput();
        if (input) input.focus();
      }, FOCUS_DELAY_MS);
    }

    function closeSearch() {
      header.classList.remove('is-searching');
      if (mobileWrap) mobileWrap.classList.remove('is-open');
      if (toggle) toggle.setAttribute('aria-expanded', 'false');
      hideResults();
      qsa('[data-yu-search-input]', header).forEach(function (input) { input.value = ''; });
      lastQuery = '';
    }

    function isOpen() {
      return header.classList.contains('is-searching') ||
        (mobileWrap && mobileWrap.classList.contains('is-open'));
    }

    function renderEmpty() {
      if (resultsInner) resultsInner.innerHTML = emptyMarkup;
    }

    function render(query) {
      if (!resultsInner) return;
      /* The predictive endpoint rejects an empty q and returns an error body
         ("param is missing or the value is empty: q"), which would then be
         injected straight into the panel. Never request without a term. */
      if (!query) {
        renderEmpty();
        return;
      }
      var url = '/search/suggest?q=' + encodeURIComponent(query) +
        '&resources[type]=product&resources[limit]=8' +
        '&section_id=predictive-search';
      fetch(url, { headers: { 'Accept': 'text/html' } })
        .then(function (res) { return res.text(); })
        .then(function (html) {
          var parsed = new DOMParser().parseFromString(html, 'text/html');
          var section = parsed.querySelector('.shopify-section') || parsed.body;
          /* Reveal BEFORE injecting: lazy-loaded thumbnails parsed into a
             still-hidden subtree have no layout box and stay deferred. */
          showResults();
          resultsInner.innerHTML = section.innerHTML;
        })
        .catch(function () {});
    }

    if (toggle) {
      toggle.addEventListener('click', function () {
        if (isOpen()) {
          closeSearch();
        } else {
          openSearch();
        }
      });
    }

    qsa('[data-yu-search-input]', header).forEach(function (input) {
      input.addEventListener('input', function () {
        var q = input.value.trim();
        if (q === lastQuery) return;
        lastQuery = q;
        window.clearTimeout(timer);
        timer = window.setTimeout(function () { render(q); }, SEARCH_DEBOUNCE_MS);
      });
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && isOpen()) closeSearch();
    });

    document.addEventListener('click', function (e) {
      if (!isOpen()) return;
      if (e.target.closest('[data-yu-header]')) return;
      closeSearch();
    });
  }

  /* ============================================================
     §4.3 MOBILE DRAWER
     Class toggle .yuk-mnav.is-open with aria-expanded on the trigger.
     Link order is deliberately different from desktop and lives in the
     markup, not here. The logo always navigates home; the drawer has its
     own visible trigger.
     ============================================================ */
  function bindMobileNav() {
    var toggle = qs('[data-yu-menu-toggle]');
    var nav = qs('[data-yu-mobile-nav]');
    if (!toggle || !nav) return;

    toggle.addEventListener('click', function () {
      var open = nav.classList.toggle('is-open');
      toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
    });

    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape' || !nav.classList.contains('is-open')) return;
      nav.classList.remove('is-open');
      toggle.setAttribute('aria-expanded', 'false');
      toggle.focus();
    });
  }

  /* ============================================================
     §4.4 STICKY QUIZ BAR
     Dismissal persists under exactly `yu_quizbar_dismissed` (value "1"),
     try/catch wrapped so private mode never throws. `?quizbar=show` forces
     the undismissed state WITHOUT writing storage (review aid). Hidden on
     the quiz page — the section itself does not render there.
     This is the only localStorage key in the theme.
     ============================================================ */
  function readDismissed() {
    try {
      return window.localStorage.getItem(QUIZBAR_KEY) === '1';
    } catch (err) {
      return false;
    }
  }

  function writeDismissed() {
    try {
      window.localStorage.setItem(QUIZBAR_KEY, '1');
    } catch (err) {
      /* private mode — dismissal is session-only, which is acceptable */
    }
  }

  function bindQuizBar() {
    var bar = qs('[data-yu-quizbar]');
    if (!bar) return;

    var forced = false;
    try {
      forced = new URLSearchParams(window.location.search).get('quizbar') === 'show';
    } catch (err) {
      forced = false;
    }

    if (forced || !readDismissed()) {
      bar.removeAttribute('hidden');
    }

    var dismiss = qs('[data-yu-quizbar-dismiss]', bar);
    if (dismiss) {
      dismiss.addEventListener('click', function () {
        bar.setAttribute('hidden', '');
        writeDismissed();
      });
    }
  }

  /* ============================================================
     §4.5 ACCORDIONS (FAQ — homepage + pillar)
     Button + aria-expanded + aria-controls panel, plus/minus icon swap.
     Panels ship VISIBLE in the markup, so with JS off every answer is
     readable; this applies each row's server-rendered aria-expanded state on
     load. As of R5-2 no row ships expanded, so every accordion starts closed.
     ============================================================ */
  var ICON_PLUS = '<path d="M5 12h14" /><path d="M12 5v14" />';
  var ICON_MINUS = '<path d="M5 12h14" />';

  function setAccordion(btn, expand) {
    var panel = document.getElementById(btn.getAttribute('aria-controls'));
    btn.setAttribute('aria-expanded', expand ? 'true' : 'false');
    if (panel) {
      if (expand) {
        panel.removeAttribute('hidden');
      } else {
        panel.setAttribute('hidden', '');
      }
    }
    var svg = btn.querySelector('[data-yu-accordion-icon] svg');
    if (svg) svg.innerHTML = expand ? ICON_MINUS : ICON_PLUS;
  }

  /* ONE row's binding, factored out so the R10.4 drawer facets in §4.7 run
     the SAME code path — same attributes, same plus/minus swap — instead of a
     second accordion implementation that could drift from this one. */
  function bindAccordion(btn) {
    setAccordion(btn, btn.getAttribute('aria-expanded') === 'true');
    btn.addEventListener('click', function () {
      setAccordion(btn, btn.getAttribute('aria-expanded') !== 'true');
    });
  }

  function bindAccordions() {
    qsa('[data-yu-accordion-trigger]').forEach(function (btn) { bindAccordion(btn); });
  }

  /* ============================================================
     §4.6 TABS (PDP: How it works / How to use)
     Roving aria-selected, 2px --ink underline on the active tab. Keyboard:
     Left/Right move between tabs. Panels are in the DOM at all times for
     SEO and toggled with `hidden` — so with JS off both panels read.
     ============================================================ */
  function bindTabs() {
    qsa('[data-yu-tabs]').forEach(function (group) {
      var tabs = qsa('[data-yu-tab]', group);
      var panels = qsa('[data-yu-tabpanel]', group);
      if (tabs.length < 2) return;

      function select(index) {
        tabs.forEach(function (tab, i) {
          var on = i === index;
          tab.setAttribute('aria-selected', on ? 'true' : 'false');
          tab.classList.toggle('is-active', on);
          tab.setAttribute('tabindex', on ? '0' : '-1');
        });
        panels.forEach(function (panel, i) {
          if (i === index) {
            panel.removeAttribute('hidden');
          } else {
            panel.setAttribute('hidden', '');
          }
        });
      }

      tabs.forEach(function (tab, i) {
        tab.addEventListener('click', function () { select(i); });
        tab.addEventListener('keydown', function (e) {
          var next = null;
          if (e.key === 'ArrowRight') next = (i + 1) % tabs.length;
          if (e.key === 'ArrowLeft') next = (i - 1 + tabs.length) % tabs.length;
          if (next === null) return;
          e.preventDefault();
          select(next);
          tabs[next].focus();
        });
      });

      select(0);
    });
  }

  /* ============================================================
     §4.7 COLLECTION FILTERS — R10
     Filters themselves are still NATIVE Shopify filter links rendered in
     Liquid — linkable, crawlable, and they survive a refresh. Nothing here
     filters, sorts or reorders anything client side.

     ONE ELEMENT, TWO PRESENTATIONS. `[data-yu-filter-sheet]` is the mobile
     bottom sheet below 760px and the left drawer at >=761px. R10.4 requires
     its DOM below the breakpoint to stay byte-identical to 23a while each
     facet group becomes a §4.5 accordion above it, from ONE copy of the
     markup — so the desktop affordances are built HERE, at runtime, behind
     the breakpoint:

       enhance()  wraps each group in a row, gives it a real accordion button
                  (bindAccordion — literally §4.5's code path), builds the
                  R10.5 footer, and puts the id on the sheet that the toolbar
                  button's aria-controls names.
       revert()   restores the panel's ORIGINAL innerHTML, captured before
                  anything touched it. Not a reconstruction — the same bytes.

     Below 760px neither runs, so a phone gets exactly what Liquid shipped.

     Close: X, backdrop, Escape — unchanged from 23a, and still delegated on
     the sheet, which is why replacing the panel's children is safe.
     ============================================================ */
  var DRAWER_ID = 'yu-filter-drawer';
  /* R10.4 ships every facet group collapsed except this one. The sheet's
     markup carries no facet param — only the label Shopify renders — so the
     group is matched on that label, compared case-insensitively. */
  var EXPANDED_GROUP = 'product type';
  var FACET_ICON =
    '<svg class="yu-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" ' +
    'stroke="currentColor" stroke-width="1.5" stroke-linecap="round" ' +
    'stroke-linejoin="round" aria-hidden="true" focusable="false">' + ICON_PLUS + '</svg>';

  function bindFilterSheet() {
    var sheet = qs('[data-yu-filter-sheet]');
    if (!sheet) return;
    var panel = qs('.yuk-sheet__panel', sheet);
    var openers = qsa('[data-yu-filter-open]');
    var desktop = window.matchMedia(DESKTOP_QUERY);
    var pristine = panel ? panel.innerHTML : '';
    var enhanced = false;
    var lastTrigger = null;
    var locked = false;
    var bodyOverflow = '';

    function setOpenState(on) {
      openers.forEach(function (btn) {
        /* Only the R10.2 toolbar button DECLARES aria-expanded. The mobile
           button ships without it in 23a and R10.7/R10.8 keep mobile
           untouched, so its markup is never written to. */
        if (btn.hasAttribute('aria-expanded')) {
          btn.setAttribute('aria-expanded', on ? 'true' : 'false');
        }
      });
    }

    /* R10.3 asks for a body scroll lock and points at the cart drawer for the
       mechanism. THE CART DRAWER HAS NONE — 23a has no scroll lock anywhere —
       so this is a new one, and it is confined to the desktop drawer:
       locking a phone's body scroll would be a mobile behaviour change, which
       R10.7/R10.8 forbid. */
    function lockScroll() {
      if (locked || !desktop.matches) return;
      bodyOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      locked = true;
    }

    function unlockScroll() {
      if (!locked) return;
      document.body.style.overflow = bodyOverflow;
      bodyOverflow = '';
      locked = false;
    }

    /* The trigger is PASSED, not inferred from document.activeElement as 23a
       did: R10.2 adds a second opener, so "the button that opened this" is now
       a real question and focus has to return to the right one. This is the
       cart drawer's own contract (§4.1 cart.open(trigger)). */
    function open(trigger) {
      lastTrigger = trigger || document.activeElement;
      sheet.classList.add('is-open');
      sheet.setAttribute('aria-hidden', 'false');
      setOpenState(true);
      lockScroll();
      var close = qs('[data-yu-filter-close]', sheet);
      if (close) close.focus();
    }

    function close() {
      sheet.classList.remove('is-open');
      sheet.setAttribute('aria-hidden', 'true');
      setOpenState(false);
      unlockScroll();
      if (lastTrigger && typeof lastTrigger.focus === 'function') lastTrigger.focus();
      lastTrigger = null;
    }

    /* R10.5 — the collection URL with every facet param dropped and the sort
       kept. Shopify names every facet param `filter.*`, so the rule is exact
       rather than a list this theme would have to keep in sync. Paging goes
       too: a cleared view starts at page one. */
    function clearUrl() {
      var url;
      try {
        url = new URL(window.location.href);
      } catch (err) {
        return window.location.pathname;
      }
      var keep = [];
      url.searchParams.forEach(function (value, key) {
        if (key.indexOf('filter.') === 0 || key === 'page') return;
        keep.push(encodeURIComponent(key) + '=' + encodeURIComponent(value));
      });
      return url.pathname + (keep.length ? '?' + keep.join('&') : '');
    }

    function enhance() {
      if (enhanced || !panel) return;
      enhanced = true;
      sheet.id = DRAWER_ID;

      /* R10.4 — each group becomes a §4.5 accordion row. The group's original
         heading is kept in the DOM and hidden rather than replaced, so the
         shape stays obvious in the inspector and the revert stays trivial. */
      qsa('.yu-eyebrow', panel).forEach(function (label, i) {
        var list = label.nextElementSibling;
        if (!list || !list.classList.contains('yuk-sheet__list')) return;

        var group = document.createElement('div');
        group.className = 'yuk-facet';
        panel.insertBefore(group, label);
        group.appendChild(label);
        group.appendChild(list);

        var name = (label.textContent || '').trim();
        var btn = document.createElement('button');
        btn.className = 'yu-faq__btn yuk-facet__btn';
        btn.type = 'button';
        btn.id = 'yu-facet-' + i + '-btn';
        btn.setAttribute('data-yu-accordion-trigger', '');
        btn.setAttribute('aria-expanded', name.toLowerCase() === EXPANDED_GROUP ? 'true' : 'false');

        list.id = 'yu-facet-' + i + '-panel';
        list.setAttribute('data-yu-accordion-panel', '');
        list.setAttribute('role', 'region');
        list.setAttribute('aria-labelledby', btn.id);
        btn.setAttribute('aria-controls', list.id);

        var text = document.createElement('span');
        text.textContent = name;
        var icon = document.createElement('span');
        icon.className = 'yu-faq__icon';
        icon.setAttribute('data-yu-accordion-icon', '');
        icon.innerHTML = FACET_ICON;
        btn.appendChild(text);
        btn.appendChild(icon);

        group.insertBefore(btn, label);
        label.hidden = true;
        bindAccordion(btn);
      });

      /* R10.5 — footer: Clear filters left, the existing confirm right. The
         confirm is the button 23a already ships; it is moved, not rebuilt, so
         its copy and its close wiring are untouched. */
      var confirm = qs('.yu-btn--primary[data-yu-filter-close]', panel);
      if (!confirm) return;
      var foot = document.createElement('div');
      foot.className = 'yuk-sheet__foot';
      panel.insertBefore(foot, confirm);

      var clear = document.createElement('a');
      clear.className = 'yu-btn yu-btn--secondary yu-btn--sm';
      clear.setAttribute('data-yu-filter-clear', '');
      clear.href = clearUrl();
      clear.textContent = 'Clear filters';

      confirm.classList.add('yu-btn--sm');
      foot.appendChild(clear);
      foot.appendChild(confirm);
    }

    function revert() {
      if (!enhanced || !panel) return;
      enhanced = false;
      unlockScroll();
      sheet.removeAttribute('id');
      panel.innerHTML = pristine;
    }

    /* CROSSING THE BREAKPOINT WHILE OPEN CLOSES FIRST. Three things break if
       it does not: revert() wipes the panel out from under the focused close
       button, so focus falls to <body> and trapFocus stops containing a
       dialog that is still aria-modal and still on screen; the scroll lock is
       either never taken (opened on mobile, widened into the drawer) or
       dropped and not retaken; and close() would later return focus to
       whichever trigger is display:none on the new side of the breakpoint.
       Closing is also what a viewport change should do to a modal — the panel
       it was is not the panel it becomes. */
    function sync() {
      if (sheet.classList.contains('is-open')) close();
      if (desktop.matches) enhance();
      else revert();
    }

    openers.forEach(function (btn) {
      btn.addEventListener('click', function () { open(btn); });
    });

    sheet.addEventListener('click', function (e) {
      if (e.target.closest('[data-yu-filter-close], [data-yu-filter-close-backdrop]')) {
        e.preventDefault();
        close();
      }
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && sheet.classList.contains('is-open')) close();
    });

    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Tab' || !sheet.classList.contains('is-open')) return;
      var trap = qs('.yuk-sheet__panel', sheet);
      if (trap) trapFocus(trap, e);
    });

    sync();
    if (typeof desktop.addEventListener === 'function') desktop.addEventListener('change', sync);
    else if (typeof desktop.addListener === 'function') desktop.addListener(sync);
  }

  /* ============================================================
     §4.7 SORT (R10.2)
     A real GET submit, never a client-side reorder. The form already carries
     the active filter values as hidden inputs (main-collection.liquid), so
     the server gets sort_by AND the facets in one navigation — and the
     control still works with JS off, which is the only reason it is a form
     rather than a change handler that rewrites location.
     ============================================================ */
  function bindSort() {
    var select = qs('[data-yu-sort]');
    if (!select || !select.form) return;
    select.addEventListener('change', function () { select.form.submit(); });
  }

  /* ============================================================
     PDP gallery — thumb swaps the main image. Purely additive; with JS
     off every image is still reachable in the DOM.
     ============================================================ */
  function bindGallery() {
    var main = qs('[data-yu-pdp-main]');
    if (!main) return;
    qsa('[data-yu-pdp-thumb]').forEach(function (thumb) {
      thumb.addEventListener('click', function () {
        main.setAttribute('src', thumb.getAttribute('data-yu-pdp-thumb'));
      });
    });
  }

  /* ---------- boot ---------- */
  onReady(function () {
    bindCart();
    bindSearch();
    bindMobileNav();
    bindQuizBar();
    bindAccordions();
    bindTabs();
    bindFilterSheet();
    bindSort();
    bindGallery();
  });
})();
